import { ArrowRight, CheckCircle2, ClipboardCheck, Loader2, Play, RotateCcw, Sprout } from 'lucide-react';
import Forecast from './Forecast';

/**
 * What to do next, in one sentence, with the button that does it.
 *
 * An agentic engagement alternates between the runtime working and the operator
 * deciding, and from the outside those two look identical: a screen that is not
 * changing. The controls do not help, because Advance means four different
 * things depending on where the engagement is, and after approving a recon plan
 * it means "now go and run the recon" rather than "start testing", which is
 * only obvious once you already know the flow.
 *
 * So this reads the state and says the one thing to do. It is derived, never
 * stored: there is no wizard step counter to drift out of sync with the
 * database, and an operator who does something out of order simply gets a
 * different sentence.
 *
 * DELIBERATELY NOT A TUTORIAL. It says what to press now, not what the whole
 * flow is, because the value is in the moment the operator is stuck and a
 * paragraph is not read at that moment.
 */

/**
 * How long a run may be silent and still be believed to be mid-turn.
 *
 * Mirrors IN_FLIGHT_SECONDS in the backend's agentic router. A turn is a model
 * call plus usually a beacon dispatch, so tens of seconds is normal and minutes
 * are not. Generous on purpose: calling a working agent dead is worse than
 * being slow to notice a dead one, because the operator acts on the first and
 * only waits on the second.
 */
const IN_FLIGHT_MS = 300 * 1000;

/**
 * Is this run actually doing something?
 *
 * THE RULE, and it is deliberately not a status read. A run mid model call and
 * a run that died a week ago are both `running`, and treating that status as
 * evidence of activity is the mistake behind four label bugs, the slot park and
 * the week pentest 7 spent claiming to work. This counts milliseconds since a
 * turn row was written.
 *
 * No last_turn_at means no turn has ever been taken. That is not activity: two
 * agents on pentest 34 were drawn and sat at zero turns for three days while
 * the roster reported them working.
 */
export function isInFlight(run, now = Date.now()) {
  if (!run?.last_turn_at) return false;
  const at = Date.parse(
    // The API serialises naive UTC. Without the marker the browser reads it as
    // local time, which in any negative offset puts the last turn in the future
    // and makes every dead run look busy.
    /[zZ]|[+-]\d{2}:?\d{2}$/.test(run.last_turn_at) ? run.last_turn_at : `${run.last_turn_at}Z`,
  );
  return Number.isFinite(at) && now - at <= IN_FLIGHT_MS;
}

/**
 * Was this run drawn recently enough that having taken no turn yet is normal?
 *
 * The warm-up window for a worker's first turn. Same UTC-marker guard as
 * isInFlight, on `started_at`: a run drawn within IN_FLIGHT_MS with no turn is
 * starting; one drawn longer ago with still no turn is stuck (the pentest 34
 * shape, and the constraint freeze from this session).
 */
export function recentlyStarted(run, now = Date.now()) {
  if (!run?.started_at) return false;
  const at = Date.parse(
    /[zZ]|[+-]\d{2}:?\d{2}$/.test(run.started_at) ? run.started_at : `${run.started_at}Z`,
  );
  return Number.isFinite(at) && now - at <= IN_FLIGHT_MS;
}

const TONE = {
  you: 'border-wasabi-300 bg-wasabi-50 text-wasabi-900',
  waiting: 'border-slate-200 bg-slate-50 text-slate-700',
  done: 'border-slate-200 bg-white text-slate-700',
};

/**
 * The whole decision, as data. Order matters: the first match wins, and the
 * cases are ordered by who is blocked, operator first.
 */
export function nextStep({ overview, hypotheses }) {
  const plan = overview?.plan || null;
  const counts = overview?.counts || {};
  const running = overview?.active || [];
  const workers = running.filter((r) => r.role !== 'lead').length;
  const leadWorking = running.some((r) => r.role === 'lead');
  const asking = overview?.awaiting_authority?.length || 0;
  const reported = overview?.awaiting_operator?.length || 0;
  // Counted from turn timestamps, never from a run status. See isInFlight.
  const busyNow = running.filter((r) => isInFlight(r)).length;
  // The engagement's own stored decision. False means nothing is scheduled,
  // which is a different thing from everything being parked and is the state
  // that used to be invisible for days at a time.
  const driving = overview?.driving !== false;
  const lead = (overview?.roster || []).find((m) => m.role === 'lead');
  const planned = counts.planned || 0;
  const queued = counts.queued || 0;
  const investigating = counts.investigating || 0;
  const settled = (counts.confirmed || 0) + (counts.refuted || 0) + (counts.abandoned || 0);

  // FIRST, because it is the ending the one successful engagement actually
  // reached and there used to be no way out of it at all.
  if (reported) {
    return {
      tone: 'you',
      title: 'The Lead has reported and stopped',
      body: 'It has told you where things stand and is waiting to be read. Acknowledging it in the decision dock below lets it carry on; it re-reads the engagement on its next turn rather than acting on anything you type.',
    };
  }

  if (asking) {
    // Deliberately terse. The dock below states the decision, names the plan
    // version and carries the buttons; this line exists to explain the STOP,
    // not to restate the ask. Three components saying the same sentence in
    // different words is what the operator was looking at.
    if (plan?.status === 'awaiting_approval') {
      return {
        tone: 'you',
        title: `Stopped: plan v${plan.version} needs your approval`,
        body: 'Nothing touches a host until you decide. Review it below.',
        action: { label: 'Review and decide', tab: 'plan', icon: ClipboardCheck },
      };
    }
    return {
      tone: 'you',
      title: 'The Lead is asking you something',
      body: 'It has stopped and cannot continue until you answer. The request is in the decision dock below.',
    };
  }

  if (plan?.status === 'awaiting_approval') {
    const recon = plan.kind === 'recon';
    return {
      tone: 'you',
      title: `Plan v${plan.version} is waiting for you`,
      body: recon
        ? 'A recon plan: scanners to run against your scope, so the Lead has something real to reason about. Nothing touches a host until you approve it.'
        : 'A test plan: the claims the Lead thinks are worth an agent. Approve it, trim steps out of it, or send it back with a note.',
      action: { label: 'Review the plan', tab: 'plan', icon: ClipboardCheck },
    };
  }

  if (workers) {
    // SPLIT ON A COUNTED FACT. This branch used to claim agents were working
    // purely because their status said `running`, which is equally true of a
    // run that died days ago: pentest 34 had two sitting at zero turns for
    // three days while this panel reported them working.
    if (busyNow) {
      return {
        tone: 'waiting',
        title: `${busyNow} agent${busyNow === 1 ? '' : 's'} working`,
        body: 'Nothing to do. Evidence appears under Angles as it is captured, and the screen updates itself.',
        spinner: true,
      };
    }
    if (!driving) {
      return {
        tone: 'you',
        title: `${workers} agent${workers === 1 ? '' : 's'} mid-run, but nothing is scheduled`,
        body: 'The engagement is not being driven, so no further turns will be taken. Nothing is lost: every run resumes from its ledger. Run the next step to start it again.',
        action: { label: 'Run next step', act: 'advance', icon: Play },
      };
    }
    // THE FIRST TURN IS NOT A STALL, and conflating the two is a real bug.
    //
    // A worker takes one to three minutes on its opening turn - a model call
    // plus a beacon dispatch - and has no `last_turn_at` until that turn lands,
    // so `busyNow` is 0 for the whole warm-up. The old code fell straight to
    // "no turn for a few minutes ... usually resolves itself" at, measured,
    // THIRTY-ONE SECONDS. That copy made a healthy warm-up and a genuine freeze
    // look identical, and it is the exact line that reassured the operator
    // during a real stall the runtime could not recover from.
    //
    // Split on whether a worker has EVER taken a turn. Drawn recently with none
    // yet is warming up; drawn long ago with none, or a last turn minutes back,
    // is the genuine stall.
    const warming = running.filter(
      (r) => r.role !== 'lead' && !r.last_turn_at && recentlyStarted(r),
    ).length;
    if (warming) {
      return {
        tone: 'waiting',
        title: `${warming} agent${warming === 1 ? '' : 's'} starting`,
        body: 'Drawn and on the first turn. A turn is a model call plus a scan, so the first one takes a minute or two; evidence appears under Angles as it lands.',
        spinner: true,
      };
    }
    return {
      tone: 'waiting',
      title: `${workers} agent${workers === 1 ? '' : 's'} mid-run, no turn landed recently`,
      body: 'No turn has landed in the last few minutes. The scheduler retries every minute and usually picks it back up; if it stays here, open Stream to see the last thing each agent did.',
      spinner: true,
    };
  }

  if (plan?.status === 'approved' && plan.kind === 'recon') {
    const steps = plan.recon_steps || 0;
    const sent = plan.recon_dispatched || 0;
    const done = plan.recon_finished || 0;
    const skipped = plan.recon_skipped || 0;

    // EVERY STEP REFUSED BEFORE DISPATCH is its own state, and it was invisible.
    //
    // On pentest 40 the Lead drafted thirteen recon steps with no target on any
    // of them. All thirteen skipped, nothing reached a beacon, and the API
    // counted a skip as both dispatched and finished, so this said "Recon is
    // finished. All 13 scanners have come back." The operator pressed Run next
    // step, the Lead reported it was blocked, the banner said finished again,
    // and round it went. Four reports and 44k tokens.
    //
    // Checked before the progress branches because it is not progress. No
    // action button either: pressing advance again cannot fix a plan whose
    // steps have no target, and offering it is what made the loop feel like
    // something was happening.
    if (skipped && !sent) {
      return {
        tone: 'you',
        title: `Recon ran nothing: ${skipped} of ${steps} step${steps === 1 ? '' : 's'} were skipped`,
        body: 'Every step was refused before dispatch, so no beacon task was created and nothing touched a host. Open the plan to read the reason recorded against each step. This needs a fix to the plan or the scope rather than another run.',
      };
    }

    // NOT STARTED IS CHECKED FIRST, and it has to be. Reading progress off the
    // approved count alone made eight approved steps with none finished look
    // like "running": the operator was shown a spinner and told the scanners
    // were working before anything had been handed to a beacon at all, and the
    // branch that would have said so was unreachable underneath it.
    if (!sent) {
      return {
        tone: 'you',
        title: steps
          ? `Recon is approved: ${steps} scanners, none dispatched yet`
          : 'Recon is approved',
        body: 'Nothing has been handed to a beacon yet, so nothing has touched the host. Run the next step to dispatch them; they then run themselves, with no agent and no model turn.',
        action: { label: 'Run next step', act: 'advance', icon: Play },
      };
    }
    if (done + skipped < steps) {
      return {
        tone: 'waiting',
        title: `Recon is running: ${done} of ${steps} finished`,
        body: 'No agent and no model turn; the scanners are just working. When the last one lands the Lead reads what they found and drafts a test plan for you to approve.',
        spinner: true,
      };
    }
    // Counts what CAME BACK, never the step total, so a partly-skipped plan
    // does not claim results it never had.
    return {
      tone: 'you',
      title: 'Recon is finished',
      body: `${done} of ${steps} scanner${steps === 1 ? '' : 's'} came back`
        + (skipped ? `, ${skipped} skipped before dispatch` : '')
        + '. Run the next step and the Lead reads what they found, frames what is worth testing, and drafts a test plan.',
      action: { label: 'Run next step', act: 'advance', icon: Play },
    };
  }

  if (queued || investigating) {
    return {
      tone: 'you',
      title: `${queued + investigating} angle${queued + investigating === 1 ? '' : 's'} approved and waiting`,
      body: 'You have agreed to this work. Running the next step puts it in front of agents.',
      action: { label: 'Run next step', act: 'advance', icon: Play },
    };
  }

  if (!lead && !plan) {
    return {
      tone: 'you',
      title: 'Nothing has started yet',
      body: 'Starting wakes the Engagement Lead. It reads your scope and the rules of engagement, and comes back with a plan for you to approve. On an engagement with no findings, expect a recon plan first.',
      // NO ACTION HERE, DELIBERATELY, AND THIS IS THE ONE STATE WHERE THAT IS
      // TRUE. The empty Plan tab below carries a single large Start button
      // instead: on a blank engagement the whole screen is one instruction,
      // and a small button in the corner of a banner is the least findable
      // place to put it.
      //
      // Mutually exclusive on purpose. PlanView has had a duplicate of this
      // button added and removed three times; the rule is one action in one
      // place, not no action in this one. Every other state below keeps its
      // Run next step here, because from then on the banner is the thing that
      // names the next move.
      startsBelow: true,
      aside: 'Propose angles, in the header above, is the other path: it turns what this engagement already knows into angles - one per existing finding, plus one per scope target - without touching a host. Those still need a plan and your approval before anything runs.',
      asideIcon: Sprout,
    };
  }

  if (planned && plan?.status !== 'awaiting_approval') {
    return {
      tone: 'you',
      title: `${planned} angle${planned === 1 ? '' : 's'} proposed, none approved`,
      body: 'They are waiting on a plan. Ask the Lead for one, then approve it.',
      action: { label: 'Ask the Lead for a plan', tab: 'plan', icon: ClipboardCheck },
    };
  }

  if (settled && !queued && !investigating && !planned) {
    const blocked = counts.blocked || 0;

    // THE LEAD STILL OWES A TURN, AND "NOTHING LEFT" SAID OTHERWISE.
    //
    // Every angle reaching a verdict is not the same as the engagement being
    // over: the Lead itself has to read them and close, which is the turn
    // Forecast's own `isFinished` waits for (it requires `lead.status ===
    // 'complete'`). Landing here with the Lead still open meant this banner
    // said "Nothing left to work" while the Forecast line directly beneath it
    // said "not finished yet, about N turns to go" on the same screen. Say
    // what is actually still outstanding instead of declaring the engagement
    // done a turn early.
    if (lead?.status !== 'complete') {
      return {
        tone: 'waiting',
        title: leadWorking ? 'The Lead is wrapping up' : 'The Lead is closing out',
        body: `${settled} angle${settled === 1 ? '' : 's'} reached a verdict`
          + (blocked ? `, and ${blocked} more ${blocked === 1 ? 'is' : 'are'} blocked` : '')
          + '. It still owes one turn to read them, write the report and close. Nothing to do until that lands.',
        spinner: leadWorking,
      };
    }

    // A BLOCKED ANGLE IS NOT SETTLED. It is not counted in `settled` above and
    // it can be reopened rather than left as a dead end, so "nothing left to
    // work" is also wrong here: there is a specific angle worth going back to.
    if (blocked) {
      return {
        tone: 'you',
        title: `${settled} angle${settled === 1 ? '' : 's'} settled, ${blocked} blocked`,
        body: 'A blocked angle can be reopened rather than left as it is. Read the report for what settled, or reopen the blocked one from its page under Angles.',
        action: { label: 'Read the report', tab: 'report', icon: CheckCircle2 },
      };
    }

    return {
      tone: 'done',
      title: 'Nothing left to work',
      body: `${settled} angle${settled === 1 ? '' : 's'} reached a verdict. Ruling something out is a result: the report says so rather than omitting it.`,
      action: { label: 'Read the report', tab: 'report', icon: CheckCircle2 },
    };
  }

  // It can finally say "the Lead is thinking" and mean it. The comment that
  // used to sit here explained why it could not: a run status says whether
  // something is PARKED, not whether anything is executing, so a Lead that
  // drafted a plan five hours ago read as `running` throughout and this panel
  // told the operator it was working. `last_turn_at` is a row that was written,
  // so the claim is now backed by something that happened.
  if (leadWorking && busyNow) {
    return {
      tone: 'waiting',
      title: 'The Lead is thinking',
      body: 'It took a turn moments ago. Its reasoning appears in the stream as it goes, and the screen updates itself.',
      spinner: true,
    };
  }

  // A CLOSED LEAD IS A DEAD END UNLESS THIS OFFERS THE WAY OUT.
  //
  // Watched on pentest 48: the Lead closed the engagement after four turns,
  // and from then on every press of Run next step returned "nothing to
  // advance: the Lead is complete". True, and useless: nothing on the screen
  // said that restarting is the move, or that it costs nothing. The runtime
  // has had `/lead/restart` since COMPLETE became restartable; the operator
  // had no way to know.
  if (lead?.status === 'complete') {
    return {
      tone: 'you',
      title: 'The Lead has closed this engagement',
      body: 'It judged there was nothing further worth testing. Restarting picks it back up: it re-reads the scope, the rules and everything already learned. Nothing is lost. If it closed too early, adding a known fact first usually gives it the thing it was missing.',
      action: { label: 'Restart the Lead', act: 'restartLead', icon: RotateCcw },
    };
  }

  return {
    tone: 'you',
    title: leadWorking ? 'The Lead is awake with nothing in flight' : 'Nothing in flight',
    body: 'No agent is on a host and no scan is running. Run the next step to give the Lead a turn.',
    action: { label: 'Run next step', act: 'advance', icon: Play },
  };
}

export default function NextStep({
  overview, hypotheses, plans, onTab, onAct, busy, tab, pending,
}) {
  // OPTIMISTIC, AND ONLY FOR AS LONG AS THE SERVER HAS SAID NOTHING. The
  // dispatch is asynchronous and the Lead's run row appears seconds later, so
  // without this the banner keeps offering the button the operator just
  // pressed. Index bounds it; see pendingSince.
  const step = pending
    ? {
      tone: 'waiting',
      title: 'Waking the Lead',
      body: 'It is reading your scope, the rules of engagement and anything this engagement already knows. That takes a moment; the screen updates itself when it comes back.',
      spinner: true,
    }
    : nextStep({ overview, hypotheses });

  // SILENT WHERE THE DECISION ALREADY IS. On the Plan tab the plan is in front
  // of the operator, its header says "Awaiting your approval" and Approve and
  // Send back sit beneath it. A banner repeating that, above a dock repeating
  // it again, is how one decision came to be stated four times on one screen.
  if (tab === 'plan' && overview?.plan?.status === 'awaiting_approval') return null;
  const Icon = step.action?.icon;
  const AsideIcon = step.asideIcon;

  return (
    <div data-testid="next-step" className={`rounded-lg border p-3 ${TONE[step.tone]}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            {(step.spinner || busy) && (
              <Loader2
                data-testid="next-step-spinner"
                className="h-3.5 w-3.5 animate-spin opacity-60"
              />
            )}
            {step.title}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed opacity-90">{step.body}</p>
          {step.aside && (
            <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug opacity-70">
              {AsideIcon && <AsideIcon className="mt-0.5 h-3 w-3 shrink-0" />}
              <span>{step.aside}</span>
            </p>
          )}
        </div>
        {step.action && (
          <button
            type="button"
            data-testid="next-step-action"
            // DISABLED FOR THE WHOLE LENGTH OF THE DISPATCH, not just while the
            // HTTP request is open. `busy` is held by Index until the follow-up
            // reload lands, which is what makes one press one turn. The operator
            // was pressing this four and five times: the advisory lock meant the
            // extra slices stood down harmlessly, so nothing broke, but the
            // screen gave him no reason to believe the first press had landed.
            disabled={!!busy}
            aria-busy={!!busy}
            onClick={() => (step.action.tab ? onTab(step.action.tab) : onAct(step.action.act))}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-wasabi-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-wasabi-500/25 transition hover:bg-wasabi-600 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-wasabi-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon && <Icon className="h-4 w-4" />}
            {busy ? 'Working...' : step.action.label}
            {!busy && <ArrowRight className="h-3 w-3 opacity-70" />}
          </button>
        )}
      </div>

      {/* WHERE THE FINISH LINE IS, on every state except the one where the
          engagement has not begun and the number would be invented.

          The operator's summarising note was that he could not tell how far
          through an engagement he was. The banner already said what to do next;
          it never said how many of these there were going to be. */}
      {overview && !step.startsBelow && (
        <div className="mt-2 border-t border-current/10 pt-2">
          <Forecast overview={overview} hypotheses={hypotheses} plans={plans} />
        </div>
      )}
    </div>
  );
}
